import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/config";
import Ornament from "@/components/Ornament";
import FancyTitle from "@/components/FancyTitle";
import { ArrowLeft, ArrowRight } from "lucide-react";

const CHURCH_IMG =
  "/img/mic-church.jpg";

export default function Church() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/blog", { params: { section: "church" } })
      .then((r) => setPosts(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  const fmt = (iso) => {
    if (!iso) return "";
    const d = new Date(iso + "T00:00:00");
    if (isNaN(d)) return iso;
    return d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  };

  return (
    <div className="min-h-screen bg-parchment-noise">
      {/* Return strip */}
      <div className="border-b border-ink/15 bg-parchment/80 sticky top-0 z-30 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-sans text-[0.7rem] uppercase tracking-[0.24em] text-ink/70 hover:text-burgundy"
            data-testid="church-back-link"
          >
            <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Invitations
          </Link>
          <div className="press-title text-xs md:text-sm uppercase tracking-[0.3em] text-ink/80">
            Part II
          </div>
        </div>
      </div>

      {/* HERO */}
      <section
        className="relative h-[70vh] min-h-[520px] w-full overflow-hidden"
        data-testid="church-hero"
      >
        <img
          src={CHURCH_IMG}
          alt="Microphone beneath the Gothic arch"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
          <FancyTitle as="h1" className="press-title text-4xl sm:text-5xl lg:text-6xl text-parchment mt-5 uppercase tracking-[0.28em] sm:tracking-[0.34em] leading-[1.2]">
            All Saints
          </FancyTitle>
          <div className="mt-5 eyebrow">Stoke Ferry</div>
        </div>
      </section>

      {/* BULLETIN */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-28" data-testid="bulletin-section">
        <div className="text-center">
          <FancyTitle as="h2" className="press-title text-3xl md:text-4xl text-ink mt-3 uppercase tracking-[0.24em]">
            Notice Board
          </FancyTitle>
          <Ornament />
        </div>

        {loading && (
          <div className="font-serif text-ink/60 text-center py-12">Loading…</div>
        )}

        {!loading && posts.length === 0 && (
          <div className="text-center py-12 mt-10" data-testid="bulletin-empty" />
        )}

        {posts.length > 0 && (
          <div className="grid md:grid-cols-2 gap-10 mt-12 border-t border-ink/20 pt-10">
            {posts.map((p) => (
              <article key={p.id} className="group" data-testid={`bulletin-card-${p.slug}`}>
                {p.cover_image && (
                  <div className="overflow-hidden">
                    <img
                      src={p.cover_image}
                      alt={p.title}
                      className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                    />
                  </div>
                )}
                <div className="mt-4">
                  <div className="font-sans text-[0.68rem] uppercase tracking-[0.22em] text-ink/60">
                    {fmt(p.published_at)}
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl text-ink mt-2 leading-tight">
                    {p.title}
                  </h3>
                  <div className="mt-3 whitespace-pre-line font-serif text-ink/85 leading-relaxed">
                    {p.content}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <footer className="border-t border-ink/15 py-10 text-center">
        <Link
          to="/"
          className="inline-flex items-center gap-2 font-sans text-[0.7rem] uppercase tracking-[0.24em] text-ink/70 hover:text-burgundy"
        >
          Return to the Invitations <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
        </Link>
      </footer>
    </div>
  );
}
